import React from 'react'
import { PageHeader } from 'components/page-header'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import { STRIPE_CUSTOMER_PORTAL_URL } from 'env'
import CheckIcon from 'components/material-symbol/icons/Check'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { PgaCoachLogo, PgaCoachPlusLogo } from './plan-logos'
import useMyPlanStyles from './styles'

const PGA_COACH_FEATURES = [
  ['Profile on PGA.com', 'Limited leads', 'Student management'],
  ['Limited offering types', 'Online payments', 'Notes'],
]

const PGA_COACH_PLUS_FEATURES = [
  [
    'Unlimited leads',
    'Group event waitlisting',
    'Personalized customer success',
    'Access to special partner opportunities',
    'Access to exclusive content',
  ],
  [
    'Unlimited offering types',
    'Promotional opportunities',
    'Dedicated concierge support',
    'First access to new features',
  ],
]

const PlanFeature = ({ label, classes }) => (
  <Box className={classes.featureItem}>
    <Box className={classes.featureIconWrap}>
      <CheckIcon className={classes.featureIcon} />
    </Box>
    <Typography className={classes.featureText}>{label}</Typography>
  </Box>
)

const PlanFeatureGrid = ({ columns, classes }) => (
  <Box className={classes.featureGrid}>
    {columns.map((column, columnIndex) => (
      <Box key={columnIndex} className={classes.featureColumn}>
        {column.map(feature => (
          <PlanFeature key={feature} label={feature} classes={classes} />
        ))}
      </Box>
    ))}
  </Box>
)

const MyPlan = () => {
  const classes = useMyPlanStyles()
  const {
    hasPaidTier: hasPaidTierSubscription,
    showSuccessModal,
    upgradeLoading: loading,
    handleUpgradeClick,
    handleDismiss,
  } = useUpgradeFlow('my-plan')

  const headerAction = hasPaidTierSubscription ? (
    <Button
      startIcon={<OpenInNewOutlinedIcon />}
      variant="contained"
      color="primary"
      target="_blank"
      href={STRIPE_CUSTOMER_PORTAL_URL}
    >
      Manage Plan
    </Button>
  ) : (
    <Button
      startIcon={<OpenInNewOutlinedIcon />}
      variant="contained"
      color="primary"
      onClick={handleUpgradeClick}
    >
      Upgrade
    </Button>
  )

  return (
    <>
      <PageHeader title="My Plan" actions={headerAction} />
      {loading && <LinearProgress />}
      <Box className={classes.contentContainer}>
        <Typography className={classes.intro}>
          Discover the perfect coaching solution with PGA Coach: start with PGA
          Coach or upgrade to PGA Coach+ for a complete business suite and
          manage everything from booking to marketing with ease.
        </Typography>

        <Box className={classes.stack}>
          <Box className={classes.card}>
            <Box className={classes.cardHeader}>
              <PgaCoachLogo className={classes.cardLogo} />
              {!hasPaidTierSubscription ? (
                <span className={classes.planBadge}>Your Plan</span>
              ) : null}
            </Box>
            <Box className={classes.cardDivider} />
            <Box className={classes.cardContent}>
              <PlanFeatureGrid columns={PGA_COACH_FEATURES} classes={classes} />
            </Box>
          </Box>

          <Box className={classes.card}>
            <Box className={classes.cardHeader}>
              <PgaCoachPlusLogo className={classes.cardLogoPlus} />
              {hasPaidTierSubscription ? (
                <span className={classes.planBadge}>Your Plan</span>
              ) : null}
            </Box>
            <Box className={classes.cardDivider} />
            <Box className={classes.cardContent}>
              <Typography className={classes.plusIntro}>
                Includes all PGA Coach features, plus:
              </Typography>
              <PlanFeatureGrid
                columns={PGA_COACH_PLUS_FEATURES}
                classes={classes}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default MyPlan
